import * as React from 'react';
import { useState } from 'react';
import { Form, Formik } from 'formik';
import { REPORT_TYPE_SERVER_TO_CLIENT_MAPPING } from '../../../../../../consts/report';
import {
  getReportDefaultGendersForMultiSelect,
  getReportAgesForMultiSelect
} from '../../../../../../helpers/multiselect/multiselect';
import { School } from '../../../../../../models/school';
import { SchoolForm } from '../../../../../../models/form';
import '../SchoolEvents.scss';
import {
  ReportActions,
  ReportCheckboxGrid,
  ReportDescription,
  ReportSection,
  ReportToggle,
  ReportToggleList
} from '../SchoolEventView/Reports/ReportFormUi';

interface Props {
  onCloseClick: () => void;
  onSubmit: (data: any, reportType: string) => void;
  school: School;
  forms: SchoolForm[];
}

// report 031
export function FixtureHistoryReport({ onCloseClick, onSubmit, school, forms }: Props) {
  const genders = getReportDefaultGendersForMultiSelect();
  const ages = getReportAgesForMultiSelect(school, forms);

  const [isAgesAndGendersChecked, setIsAgesAndGendersChecked] = useState(true);

  const requestInitial = {
    isShowInterSchool: true,
    isShowHouses: true,
    isShowIntraSchool: true,
    isShowTournament: true,
    genders,
    ages,
    isSelectAllAges: true
  };

  const onGendersChange = (index, values, setFieldValue) => {
    const genderItems = values.genders;
    const ageItems = values.ages;

    const genderItemsUpdated = genderItems.map((genderItem, genderIndex) => {
      return genderIndex === index
        ? {
            ...genderItem,
            isChecked: !genderItem.isChecked
          }
        : genderItem;
    });

    const gendersChecked = genderItemsUpdated.map(gender => gender.isChecked);
    const agesChecked = ageItems.map(age => age.isChecked);
    const isCheckedGendersExist = gendersChecked.includes(true);
    const isCheckedAgesExist = agesChecked.includes(true);

    setIsAgesAndGendersChecked(isCheckedGendersExist && isCheckedAgesExist);
    setFieldValue('genders', genderItemsUpdated);
  };

  const onAgesChange = (index, values, setFieldValue) => {
    const ageItems = values.ages;
    const genderItems = values.genders;

    const ageItemsUpdated = ageItems.map((ageItem, ageIndex) => {
      return ageIndex === index
        ? {
            ...ageItem,
            isChecked: !ageItem.isChecked
          }
        : ageItem;
    });

    const gendersChecked = genderItems.map(gender => gender.isChecked);
    const agesChecked = ageItemsUpdated.map(age => age.isChecked);
    const isCheckedGendersExist = gendersChecked.includes(true);
    const isCheckedAgesExist = agesChecked.includes(true);

    const isSelectAllAges = ageItemsUpdated.every(age => age.isChecked === true);

    setIsAgesAndGendersChecked(isCheckedAgesExist && isCheckedGendersExist);
    setFieldValue('isSelectAllAges', isSelectAllAges);
    setFieldValue('ages', ageItemsUpdated);
  };

  const onSelectAllAgesChange = (event, values, setFieldValue) => {
    const isSelectAllAges = event.target.checked;
    const ageItems = values.ages;
    const genderItems = values.genders;

    const ageItemsUpdated = ageItems.map(age => {
      return {
        ...age,
        isChecked: isSelectAllAges
      };
    });

    const gendersChecked = genderItems.map(gender => gender.isChecked);
    const agesChecked = ageItemsUpdated.map(age => age.isChecked);
    const isCheckedGendersExist = gendersChecked.includes(true);
    const isCheckedAgesExist = agesChecked.includes(true);

    setIsAgesAndGendersChecked(isCheckedAgesExist && isCheckedGendersExist);
    setFieldValue('isSelectAllAges', isSelectAllAges);
    setFieldValue('ages', ageItemsUpdated);
  };

  return (
    <Formik
      onSubmit={values => onSubmit(values, REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.FIXTURE_HISTORY)}
      initialValues={requestInitial}
    >
      {({ values, setFieldValue }) => {
        const hasFixtureType =
          values.isShowInterSchool || values.isShowHouses || values.isShowIntraSchool || values.isShowTournament;
        const status = !hasFixtureType
          ? 'Select at least one fixture type.'
          : !isAgesAndGendersChecked
          ? 'Select at least one gender and one age group.'
          : 'Ready to generate';

        return (
          <Form className="eReportForm">
            <ReportDescription>
              Compare the number of recorded fixtures across academic years. The current year includes both completed
              and scheduled fixtures.
            </ReportDescription>
            <ReportSection number={1} title="Fixture types" hint="Choose which kinds of fixtures to include.">
              <ReportToggleList>
                <ReportToggle
                  name="isShowInterSchool"
                  label="Inter-school fixtures"
                  hint="Fixtures against another school."
                  value={values.isShowInterSchool}
                  onChange={value => setFieldValue('isShowInterSchool', value)}
                />
                <ReportToggle
                  name="isShowHouses"
                  label="House fixtures"
                  hint="Internal fixtures between houses."
                  value={values.isShowHouses}
                  onChange={value => setFieldValue('isShowHouses', value)}
                />
                <ReportToggle
                  name="isShowIntraSchool"
                  label="Internal fixtures"
                  hint="Other internal school fixtures."
                  value={values.isShowIntraSchool}
                  onChange={value => setFieldValue('isShowIntraSchool', value)}
                />
                <ReportToggle
                  name="isShowTournament"
                  label="Tournament events"
                  hint="Games from tournaments your school participated in."
                  value={values.isShowTournament}
                  onChange={value => setFieldValue('isShowTournament', value)}
                />
              </ReportToggleList>
            </ReportSection>
            <ReportSection number={2} title="Gender" hint="Select at least one gender category.">
              <ReportCheckboxGrid
                items={values.genders}
                onChange={index => onGendersChange(index, values, setFieldValue)}
              />
            </ReportSection>
            <ReportSection number={3} title="Age groups" hint="Select the age groups included in the report.">
              <ReportToggleList>
                <ReportToggle
                  name="isSelectAllAges"
                  label="Select all ages"
                  hint="Include every available age group."
                  value={values.isSelectAllAges}
                  onChange={value => onSelectAllAgesChange({ target: { checked: value } }, values, setFieldValue)}
                />
              </ReportToggleList>
              <ReportCheckboxGrid items={values.ages} onChange={index => onAgesChange(index, values, setFieldValue)} />
            </ReportSection>
            <ReportActions
              onCancel={onCloseClick}
              disabled={!isAgesAndGendersChecked || !hasFixtureType}
              status={status}
            />
          </Form>
        );
      }}
    </Formik>
  );
}
